import { Actions, createEffect, ofType } from '@ngrx/effects';

import { inject } from '@angular/core';
import { VersionReviewService } from '../services/version-review.service';
import {
  catchError,
  exhaustMap,
  from,
  map,
  of,
  switchMap,
  tap,
  timeout,
  throwError,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/reducers';
import { selectMinVersion, VersionReviewActions } from './version-review.store';
import { Device } from '@capacitor/device';
import { take } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { MobileVersionReviewRoutesEnum } from '../routes/mobile-version-review-routes.enum';
import { Router } from '@angular/router';
import { BiometricsActions } from '../../biometrics/store/biometrics.store';

export const getVersionParameters$ = createEffect(
  () => {
    const actions$ = inject(Actions);
    const versionReviewService = inject(VersionReviewService);
    const store = inject(Store<AppState>);

    return actions$.pipe(
      ofType(VersionReviewActions.requestVersionParameters),
      tap(() => {
        store.dispatch(
          VersionReviewActions.requestingVersionParameters({
            requestingVersionParameters: true,
          })
        );
      }),
      exhaustMap(() =>
        versionReviewService.getLatestVersions().pipe(
          map(({ content }) => {
            return VersionReviewActions.setVersionParameters({
              versionRequirements: content,
            });
          }),
          catchError(error => {
            console.warn(
              '[VersionReview] Error getting version parameters:',
              error
            );
            return of(
              VersionReviewActions.setVersionParameters({
                versionRequirements: [],
              })
            );
          })
        )
      )
    );
  },
  { functional: true }
);

export const setVersionParameters$ = createEffect(
  () => {
    const actions$ = inject(Actions);
    const store = inject(Store<AppState>);
    const router = inject(Router);
    return actions$.pipe(
      ofType(VersionReviewActions.setVersionParameters),
      switchMap(() =>
        from(Device.getInfo()).pipe(
          take(1),
          timeout({
            first: 5000,
            with: () =>
              throwError(() => new Error('Device.getInfo timeout after 5s')),
          }),
          catchError(error => {
            console.warn(
              '[VersionReview] Device.getInfo failed or timed out, using fallback values:',
              error
            );
            return of({
              operatingSystem: 'ios' as const,
              name: 'Unknown Device',
              model: 'Unknown',
              platform: 'ios',
              webViewVersion: 'Unknown',
            });
          })
        )
      ),
      switchMap(info => {
        console.log('[VersionReview] Device info received:', info);
        const [mayor, minor, fix] = environment.version.toString().split('.');
        const myCompleteVersionNumber =
          parseInt(mayor) * 1000000 + parseInt(minor) * 1000 + parseInt(fix);
        const minimumVersion = store.selectSignal(
          selectMinVersion(info.operatingSystem)
        )();
        console.log('[VersionReview] Version check:', {
          myVersion: myCompleteVersionNumber,
          minimumVersion,
        });

        if (myCompleteVersionNumber < minimumVersion) {
          console.log(
            '[VersionReview] Version too old, redirecting to force update'
          );
          router
            .navigateByUrl(MobileVersionReviewRoutesEnum.root)
            .catch(err => {
              console.error('[VersionReview] Navigation error:', err);
              // Si la navegación falla, continuar con el flujo normal
              store.dispatch(BiometricsActions.initializeBiometricsStore());
            });
          return of(null);
        }

        console.log('[VersionReview] Version OK, initializing biometrics');
        store.dispatch(BiometricsActions.initializeBiometricsStore());
        return of(null);
      }),
      catchError(error => {
        console.error('[VersionReview] Error in setVersionParameters:', error);
        store.dispatch(BiometricsActions.initializeBiometricsStore());
        return of(null);
      })
    );
  },
  {
    functional: true,
    dispatch: false,
  }
);
